import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import FileDownloadButton, {FileExtension} from 'Shared/components/FileDownloadButton';
import {renderAsyncView} from 'Shared/components/RenderAsync';
import {RequestMethod} from 'Shared/utilities/fetchHelpers';
import {toSentenceCase} from 'Shared/utilities/stringFormatHelpers';
import {A360CommissionSection} from '../CommissionSection';
import {A360CommissionsAlert, A360CommissionsSectionError, A360CommissionsSectionNoRecordsFound, LoadingSpinner} from '../Placeholders';
import {PaymentReceipt} from '../types';
import {getPaymentReceipts} from 'Agent/Commissions/ducks/a360Commissions';
import {isAsyncServerError} from 'Core/models/Async';

export const PaymentReceipts = () => {
  const {paymentReceipts} = useSelector((state: ReducerState) => state.a360Commissions);

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getPaymentReceipts());
  }, []);

  return (
    <A360CommissionSection
      title="Payment Receipts"
      className="a360-commissions__payment-receipts"
      withSeparator
      aria-label="payment receipts"
    >
      {isAsyncServerError(paymentReceipts) ? (
        <A360CommissionsSectionError error={paymentReceipts.errorDetails} />
      ) : (
        renderAsyncView(
          paymentReceipts,
          paymentReceiptsList => (
            <div>
              {Array.isArray(paymentReceiptsList) && paymentReceiptsList.length > 0 ? (
                <>
                  {paymentReceiptsList.map(paymentReceipt => (
                    <PaymentReceiptLink paymentReceipt={paymentReceipt} key={paymentReceipt.fileName} />
                  ))}
                  {Array.isArray(paymentReceiptsList) && paymentReceiptsList.length > 0 && (
                    <PaymentReceiptInfo paymentReceiptsList={paymentReceiptsList} />
                  )}
                </>
              ) : (
                <A360CommissionsSectionNoRecordsFound />
              )}
            </div>
          ),
          LoadingSpinner
        )
      )}
    </A360CommissionSection>
  );
};

const PaymentReceiptLink = ({paymentReceipt}: {paymentReceipt: PaymentReceipt}) => {
  const [udpDocType, contentId] = paymentReceipt?.fileName?.split(':') ?? ['', ''];

  const downloadProps = {
    'aria-label': `${paymentReceipt.statementDate} Payment Receipt`,
    children: toSentenceCase(`${paymentReceipt.statementDate} Payment Receipt`),
    displayTooltip: false,
    downloadedFileName: `${paymentReceipt.statementDate}_Payment_Receipt`,
    extension: FileExtension.PDF,
    gridOverrideClassName: 'a360-commissions__payment-receipts-link',
    relativeFileLocation: '/api/documents/v1/documentContent',
    requestMethod: RequestMethod.POST,
    requestPayload: {
      retrieveContentRequest: {
        payload: {
          contentId: contentId,
          docType: udpDocType,
        },
      },
    },
  };

  return <FileDownloadButton {...downloadProps} />;
};

const PaymentReceiptInfo = ({paymentReceiptsList}: {paymentReceiptsList: PaymentReceipt[]}) => {
  return (
    !isNaN(Number(paymentReceiptsList?.[0]?.numberOfRecords)) &&
    Number(paymentReceiptsList?.[0]?.numberOfRecords) > 15 && (
      <A360CommissionsAlert message="Please refine filter criteria to view more payment receipts." />
    )
  );
};
